import React, { useState } from 'react';

interface Section {
    id: string;
    title: string;
    body: React.ReactNode;
}

const SECTIONS: Section[] = [
    {
        id: 'overview',
        title: 'What Advital Sync does',
        body: (
            <>
                <p>
                    Advital Sync keeps your EMR (your clinic's scheduling/records system) and Ad Vital in sync.
                    For appointments to flow correctly between the two systems, Advital Sync needs to know how
                    each piece of information in your EMR corresponds to the matching piece of information
                    in Ad Vital &mdash; for example, which Ad Vital calendar an EMR appointment type belongs to,
                    or which Ad Vital user an EMR practitioner is.
                </p>
                <p>
                    There are four things that need to be matched between the two systems:
                </p>
                <ul>
                    <li><strong>Appointments</strong> &mdash; EMR appointment type (and subtype) &rarr; Ad Vital calendar</li>
                    <li><strong>Providers</strong> &mdash; EMR practitioner &rarr; Ad Vital user</li>
                    <li><strong>Status</strong> &mdash; EMR appointment status &rarr; Ad Vital status</li>
                    <li><strong>Services</strong> &mdash; Ad Vital service &rarr; Ad Vital calendar</li>
                </ul>
                <p>
                    For each of these, Advital Sync can suggest matches automatically using AI, and you simply
                    review and approve them. You can also create matches yourself at any time.
                </p>
            </>
        ),
    },
    {
        id: 'signing-in',
        title: 'Signing in',
        body: (
            <>
                <p>
                    Enter your username and password on the sign-in screen and select <strong>Sign in</strong>.
                    If your details are incorrect, an error message appears above the form so you can try again.
                </p>
                <p>
                    Once signed in, your session stays active in this browser. If you're ever signed out
                    automatically, just sign in again to pick up where you left off.
                </p>
            </>
        ),
    },
    {
        id: 'location',
        title: 'Choosing a location',
        body: (
            <>
                <p>
                    At the top of every page is a <strong>Location</strong> selector. If your account has
                    access to more than one clinic location, use it to switch between them &mdash; everything
                    on screen (the dashboard, mapping lists, and settings) always reflects the location
                    currently selected.
                </p>
                <p>
                    Each location shows which EMR it's connected to (for example, its EMR name) next to its
                    Ad Vital location ID, so you can confirm you're looking at the right one.
                </p>
            </>
        ),
    },
    {
        id: 'dashboard',
        title: 'Dashboard: sync overview',
        body: (
            <>
                <p>
                    The Dashboard is the home screen. It shows a simple diagram of your EMR flowing into
                    Advital Sync and out to Ad Vital, with a running count of how many of each item type (Appointments,
                    Providers, Status, Services) are fully mapped for the currently selected location.
                </p>
                <p>
                    Below the diagram, <strong>Mapping coverage</strong> cards show, for each of the four
                    item types:
                </p>
                <ul>
                    <li>How many are mapped out of the total (e.g. "8 / 10")</li>
                    <li>A progress bar toward full coverage</li>
                    <li>How many are still <strong>pending</strong> review, shown in an amber badge</li>
                    <li>How many were <strong>rejected</strong>, shown in a red badge</li>
                </ul>
                <p>
                    Click any card to jump straight to that item type's mapping page.
                </p>
            </>
        ),
    },
    {
        id: 'syncing',
        title: 'Keeping data up to date',
        body: (
            <>
                <p>
                    Advital Sync needs a fresh copy of your EMR's locations and records to know what there is to
                    match. The <strong>Sync</strong> button in the top bar opens two options:
                </p>
                <ul>
                    <li>
                        <strong>Sync Locations</strong> &mdash; pulls in any new or updated clinic locations from
                        your EMR so they show up in the Location selector.
                    </li>
                    <li>
                        <strong>Sync Data</strong> &mdash; refreshes the underlying records (practitioners, services,
                        appointment types, statuses) so the mapping pages have the latest information to work
                        with. This can take a little while &mdash; the button shows its progress (e.g. "Running&hellip;")
                        until it finishes, and you'll get a confirmation message when it's done.
                    </li>
                </ul>
                <p>
                    The <strong>Last sync</strong> label next to the button shows how long ago data was last
                    refreshed.
                </p>
            </>
        ),
    },
    {
        id: 'ai-review',
        title: 'Reviewing AI-suggested mappings',
        body: (
            <>
                <p>
                    Each of the four mapping pages (Appointments, Providers, Status, Services) has an
                    <strong> AI Review</strong> mode, selected using the toggle at the top-right of the page.
                    This is where Advital Sync's suggested matches show up for you to review.
                </p>
                <p><strong>Generating suggestions</strong></p>
                <p>
                    Select <strong>Run AI mapping</strong> to have Advital Sync look at your unmatched EMR items and
                    suggest the best match for each one in Ad Vital. New suggestions appear in the list
                    automatically as they're ready &mdash; you don't need to refresh the page.
                </p>
                <p><strong>Reading a suggestion row</strong></p>
                <p>Each row shows, from left to right:</p>
                <ul>
                    <li>The EMR item (name, and any extra detail like specialty or ID)</li>
                    <li>
                        The suggested Ad Vital match &mdash; or a <strong>"No match &mdash; map manually"</strong> notice
                        if Advital Sync couldn't find a confident match on its own
                    </li>
                    <li>A <strong>confidence score</strong> (see below)</li>
                    <li>A short summary of <strong>why this match</strong> was suggested, with a "View" link for the full explanation</li>
                    <li>The current <strong>status</strong> (Pending, Approved, or Rejected) and action buttons</li>
                </ul>
                <p><strong>Confidence scores</strong></p>
                <p>
                    Every suggestion gets a score from 0&ndash;100% showing how sure Advital Sync is:
                </p>
                <ul>
                    <li><strong>High (90% and up)</strong> &mdash; shown in green; safe to approve in most cases</li>
                    <li><strong>Medium (70&ndash;89%)</strong> &mdash; shown in amber/orange; worth a quick double-check</li>
                    <li><strong>Low (below 70%)</strong> &mdash; shown in red; review carefully before approving</li>
                </ul>
                <p>
                    You can hide the confidence column entirely using the <strong>Confidence</strong> switch above
                    the table if you'd rather not see it.
                </p>
                <p><strong>Seeing why a match was suggested</strong></p>
                <p>
                    Select <strong>View</strong> next to any suggestion's reasoning to open a window explaining,
                    in plain language, what Advital Sync matched on (such as matching names, emails, phone numbers,
                    or identifiers) and why it chose that confidence level.
                </p>
                <p><strong>Approving, rejecting, editing, and deleting</strong></p>
                <ul>
                    <li><strong>Approve (check mark)</strong> &mdash; confirms the suggested match is correct.</li>
                    <li><strong>Reject (X)</strong> &mdash; marks the suggestion as wrong. It stays visible with a "Rejected" label.</li>
                    <li>
                        <strong>Edit / remap (pencil)</strong> &mdash; lets you pick a different Ad Vital match for that
                        EMR item, in case Advital Sync's suggestion (or a previous approval) wasn't right.
                    </li>
                    <li>
                        <strong>Delete (trash can)</strong> &mdash; removes the suggestion entirely. You'll be asked to
                        confirm ("Yes" / "No") before it's deleted.
                    </li>
                </ul>
                <p><strong>Approving several at once</strong></p>
                <p>
                    Select <strong>Approve all high-confidence</strong> to instantly approve every pending
                    suggestion scored 90% or above, without reviewing each one individually. This is a fast
                    way to clear out the matches Advital Sync is most sure about.
                </p>
                <p><strong>Searching, filtering, and paging</strong></p>
                <p>
                    Use the search box to find a specific item by name. The dropdown row below the column
                    headers lets you filter by EMR item, Ad Vital item, confidence level, or status
                    (Pending / Approved / Rejected) &mdash; select <strong>Clear filters</strong> to reset them all.
                    Results are shown 20 at a time, with page controls at the bottom of the list.
                </p>
            </>
        ),
    },
    {
        id: 'manual',
        title: 'Mapping manually',
        body: (
            <>
                <p>
                    Switch a mapping page to <strong>Manual</strong> mode when you'd rather pick a match yourself
                    instead of relying on an AI suggestion &mdash; for example, for an item Advital Sync couldn't
                    confidently match.
                </p>
                <p>
                    You'll see two searchable lists side by side: your EMR items on the left and your Ad Vital
                    items on the right. Select one item in each list, then select <strong>Map selected</strong> to
                    create the match. Matches you make during your current session appear in a
                    "Mapped this session" list below, so you can keep track of what you've just done.
                </p>
            </>
        ),
    },
    {
        id: 'providers',
        title: 'Provider mapping',
        body: (
            <p>
                Matches each practitioner in your EMR to the Ad Vital user account that should handle their
                appointments, using name, specialty, and identifiers such as NPI number where available. Once
                approved, appointments for that practitioner will be assigned to the correct Ad Vital user.
            </p>
        ),
    },
    {
        id: 'status',
        title: 'Status mapping',
        body: (
            <p>
                Matches each appointment status used in your EMR (such as "Confirmed", "Cancelled", or
                "No-show") to the equivalent status in Ad Vital, so an appointment's state stays consistent
                no matter which system it's viewed from.
            </p>
        ),
    },
    {
        id: 'appointments',
        title: 'Appointment type mapping',
        body: (
            <>
                <p>
                    Matches each appointment type (and, where your EMR has them, subtype &mdash; shown as
                    "Type - Subtype") to the Ad Vital calendar that should be used for that kind of visit.
                </p>
                <p><strong>When a calendar is already connected both ways</strong></p>
                <p>
                    Calendars sync in two directions: appointments come from your EMR onto the calendar, and
                    bookings made in Ad Vital go back to your EMR. A calendar can only send bookings back to
                    <em> one</em> EMR appointment type. If you map a type to a calendar that already sends its
                    bookings back to a different type, Advital Sync will pause and ask you to choose:
                </p>
                <ul>
                    <li>
                        <strong>Add this appt to calendar</strong> &mdash; appointments from your EMR still appear on
                        this calendar, but Ad Vital bookings on it keep going back as the original appointment type.
                    </li>
                    <li>
                        <strong>Override existing mapping</strong> &mdash; Ad Vital bookings on this calendar now go back
                        to your EMR as this appointment type, replacing the old one.
                    </li>
                    <li><strong>Cancel</strong> &mdash; backs out without making any change.</li>
                </ul>
            </>
        ),
    },
    {
        id: 'services',
        title: 'Service mapping',
        body: (
            <p>
                Matches each service offered in your Ad Vital to the Ad Vital calendar it should be booked
                against, so services booked in either system land on the correct calendar.
            </p>
        ),
    },
    {
        id: 'settings',
        title: 'Settings',
        body: (
            <>
                <p>
                    The Settings page lists every location and lets you set its <strong>Location EMR ID</strong> &mdash;
                    an identifier some EMRs require to correctly record which physical location an appointment
                    belongs to. If your EMR doesn't need this, you can leave it blank.
                </p>
                <p>Type the ID into the field and click elsewhere on the page &mdash; it saves automatically.</p>
            </>
        ),
    },
    {
        id: 'tips',
        title: 'Tips',
        body: (
            <ul>
                <li>Start with <strong>Approve all high-confidence</strong> to quickly clear the easy matches, then work through the remaining Medium/Low ones by hand.</li>
                <li>If an item shows "No match &mdash; map manually," switch to Manual mode to pair it yourself.</li>
                <li>Use <strong>View</strong> on the reasoning before approving a Medium or Low confidence match &mdash; it tells you exactly what did and didn't line up.</li>
                <li>If something looks out of date, use <strong>Sync</strong> in the top bar to refresh your EMR data before re-running AI mapping.</li>
                <li>Made a mistake? Use the pencil icon to remap an item at any time, even after it's been approved.</li>
            </ul>
        ),
    },
];

interface HelpModalProps {
    open: boolean;
    onClose: () => void;
}

export const HelpModal: React.FC<HelpModalProps> = ({ open, onClose }) => {
    const [activeId, setActiveId] = useState(SECTIONS[0].id);

    if (!open) return null;

    const active = SECTIONS.find(s => s.id === activeId) ?? SECTIONS[0];

    return (
        <div
            onClick={onClose}
            className="mapper-ui font-sans text-mapper-text fixed inset-0 flex items-center justify-center z-[120] p-5"
            style={{ background: 'rgba(24,33,48,.42)' }}
        >
            <div
                onClick={e => e.stopPropagation()}
                className="bg-mapper-white rounded-2xl w-full max-w-[880px] max-h-[85vh] overflow-hidden shadow-mapper-modal flex flex-col"
            >
                <div className="px-6 py-5 border-b border-mapper-border-3 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-[9px]">
                        <div className="w-7 h-7 rounded-lg bg-mapper-teal-bg-2 flex items-center justify-center">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0E8577" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="9" />
                                <path d="M9.5 9a2.5 2.5 0 0 1 4.9.8c0 1.7-2.4 1.7-2.4 3.4" />
                                <path d="M12 17v.5" />
                            </svg>
                        </div>
                        <h3 className="text-base font-bold m-0">Help &amp; guide</h3>
                    </div>
                    <button onClick={onClose} className="w-[30px] h-[30px] rounded-lg text-mapper-muted-3 text-xl flex items-center justify-center">×</button>
                </div>

                <div className="flex flex-1 min-h-0">
                    <nav className="w-[220px] shrink-0 border-r border-mapper-border-3 bg-mapper-surface overflow-y-auto py-3 px-3">
                        {SECTIONS.map(s => (
                            <button
                                key={s.id}
                                onClick={() => setActiveId(s.id)}
                                className={`block w-full text-left px-[12px] py-[9px] rounded-[9px] text-[13px] font-medium mb-[2px] ${
                                    s.id === activeId ? 'bg-mapper-teal-bg-2 text-mapper-teal-hover font-semibold' : 'text-mapper-body-text'
                                }`}
                            >
                                {s.title}
                            </button>
                        ))}
                    </nav>

                    <div className="flex-1 overflow-y-auto px-7 py-6">
                        <h2 className="text-lg font-extrabold tracking-[-0.3px] mb-4">{active.title}</h2>
                        <div className="text-[13.5px] leading-[1.7] text-mapper-body-text-3 space-y-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_li]:leading-[1.6] [&_strong]:text-mapper-text [&_p]:m-0">
                            {active.body}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
